import React, { useState, useEffect } from "react";
import { PageGuard } from "@/components/shared/PlanGuard";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import SafetyDocumentManager from "@/components/prl/SafetyDocumentManager.jsx";
import SafetyTrainingList from "@/components/prl/SafetyTrainingList.jsx";
import WorkIncidentList from "@/components/prl/WorkIncidentList.jsx";
import EquipmentMaintenanceList from "@/components/prl/EquipmentMaintenanceList.jsx";
import { AlertTriangle, FileText, Users, AlertCircle, Wrench } from "lucide-react";

export default function WorkerSafetyManagement() {
  const { currentVenue, userRole, loading: venueLoading } = useVenue();
  const [documents, setDocuments] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const isAdmin = userRole === "superadmin" || userRole === "admin";

  useEffect(() => {
    if (currentVenue) loadAll();
  }, [currentVenue]);

  const loadAll = async () => {
    setLoading(true);
    const [docs, train, inc, maint] = await Promise.all([
      hosys.entities.SafetyDocument.filter({ venue_id: currentVenue.id }, "-created_date"),
      hosys.entities.SafetyTraining.filter({ venue_id: currentVenue.id }, "-training_date"),
      hosys.entities.WorkIncident.filter({ venue_id: currentVenue.id }, "-incident_date"),
      hosys.entities.EquipmentMaintenance.filter({ venue_id: currentVenue.id }, "-maintenance_date"),
    ]);
    setDocuments(docs);
    setTrainings(train);
    setIncidents(inc);
    setMaintenance(maint);
    setLoading(false);
  };

  if (venueLoading || loading) return <QuickLoader />;

  const openIncidents = incidents.filter(i => i.status !== "closed").length;
  const expiredTrainings = trainings.filter(t => t.status === "expired").length;
  const pendingMaintenance = maintenance.filter(m => m.status === "pending").length;
  const criticalIncidents = incidents.filter(i => i.severity === "critical" && i.status !== "closed").length;

  const content = (
    <div className="space-y-5">
      <PageHeader
        title="Prevenció de Riscos Laborals"
        subtitle={`${currentVenue?.name} · Gestió integrada de seguretat laboral`}
      />

      {/* Alerts */}
      {(criticalIncidents > 0 || openIncidents > 0 || expiredTrainings > 0) && (
        <div className="space-y-2">
          {criticalIncidents > 0 && (
            <div className="rounded-2xl p-3 flex items-center gap-3 bg-red-50 border border-red-200 text-red-800">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm font-medium">{criticalIncidents} incident(s) crític(s) sense resoldre.</p>
            </div>
          )}
          {expiredTrainings > 0 && (
            <div className="rounded-2xl p-3 flex items-center gap-3 bg-amber-50 border border-amber-200 text-amber-800">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm font-medium">{expiredTrainings} formació(ns) caducada(es).</p>
            </div>
          )}
          {openIncidents > 0 && (
            <div className="rounded-2xl p-3 flex items-center gap-3 bg-orange-50 border border-orange-200 text-orange-800">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <p className="text-sm font-medium">{openIncidents} incident(s) obert(s) en investigació.</p>
            </div>
          )}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard icon={FileText} label="Documents" value={documents.length} color="text-blue-600" bg="bg-blue-50" />
        <KpiCard icon={Users} label="Formacions" value={trainings.length} color="text-emerald-600" bg="bg-emerald-50" />
        <KpiCard icon={AlertCircle} label="Incidents" value={openIncidents} color="text-red-600" bg="bg-red-50" />
        <KpiCard icon={Wrench} label="Manteniments" value={pendingMaintenance} color="text-purple-600" bg="bg-purple-50" />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 sm:grid-cols-4 h-auto gap-1 p-1 bg-gray-100 rounded-xl">
          <TabsTrigger value="overview" className="rounded-lg text-xs py-2">Resum</TabsTrigger>
          <TabsTrigger value="documents" className="rounded-lg text-xs py-2">Documentació</TabsTrigger>
          <TabsTrigger value="training" className="rounded-lg text-xs py-2">Formació</TabsTrigger>
          <TabsTrigger value="incidents" className="rounded-lg text-xs py-2">
            Incidents
            {openIncidents > 0 && <span className="ml-1 text-[10px] bg-red-100 text-red-600 rounded-full px-1.5">{openIncidents}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <OverviewTab documents={documents} trainings={trainings} incidents={incidents} maintenance={maintenance} onSelectTab={setActiveTab} />
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <SafetyDocumentManager venue_id={currentVenue.id} documents={documents} onChanged={loadAll} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="training" className="mt-4">
          <div className="space-y-4">
            <SafetyTrainingList venue_id={currentVenue.id} trainings={trainings} onChanged={loadAll} isAdmin={isAdmin} />
          </div>
        </TabsContent>

        <TabsContent value="incidents" className="mt-4">
          <div className="space-y-4">
            <WorkIncidentList venue_id={currentVenue.id} incidents={incidents} onChanged={loadAll} isAdmin={isAdmin} />
            <EquipmentMaintenanceList venue_id={currentVenue.id} maintenance={maintenance} onChanged={loadAll} isAdmin={isAdmin} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
  return <PageGuard moduleCode="prl">{content}</PageGuard>;
}

function KpiCard({ icon: Icon, label, value, color, bg }) {
  return (
    <Card className={`p-4 border-0 shadow-sm ${bg}`}>
      <div className="flex items-center gap-3">
        <Icon className={`w-6 h-6 ${color}`} />
        <div>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </div>
    </Card>
  );
}

function OverviewTab({ documents, trainings, incidents, maintenance, onSelectTab }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <OverviewCard
        title="Documentació de Seguretat"
        value={documents.length}
        icon={FileText}
        color="blue"
        items={documents.slice(0, 3).map(d => ({ label: d.title, status: d.status }))}
        onClick={() => onSelectTab("documents")}
      />
      <OverviewCard
        title="Formacions Registrades"
        value={trainings.length}
        icon={Users}
        color="emerald"
        items={trainings.slice(0, 3).map(t => ({ label: t.worker_name, status: t.status }))}
        onClick={() => onSelectTab("training")}
      />
      <OverviewCard
        title="Incidents Laborals"
        value={incidents.filter(i => i.status !== "closed").length}
        icon={AlertCircle}
        color="red"
        items={incidents.slice(0, 3).map(i => ({ label: i.title, status: i.severity }))}
        onClick={() => onSelectTab("incidents")}
      />
      <OverviewCard
        title="Manteniment d'Equips"
        value={maintenance.filter(m => m.status === "pending").length}
        icon={Wrench}
        color="purple"
        items={maintenance.slice(0, 3).map(m => ({ label: m.equipment_name, status: m.status }))}
        onClick={() => onSelectTab("incidents")}
      />
    </div>
  );
}

function OverviewCard({ title, value, icon: Icon, color, items, onClick }) {
  const colorMap = {
    blue: { bg: "bg-blue-50", text: "text-blue-600", badge: "bg-blue-100 text-blue-700" },
    emerald: { bg: "bg-emerald-50", text: "text-emerald-600", badge: "bg-emerald-100 text-emerald-700" },
    red: { bg: "bg-red-50", text: "text-red-600", badge: "bg-red-100 text-red-700" },
    purple: { bg: "bg-purple-50", text: "text-purple-600", badge: "bg-purple-100 text-purple-700" },
  };
  const c = colorMap[color];
  return (
    <Card className={`p-5 border-0 shadow-sm cursor-pointer hover:shadow-md transition-all ${c.bg}`} onClick={onClick}>
      <div className="flex items-start gap-3 mb-4">
        <Icon className={`w-8 h-8 ${c.text}`} />
        <div>
          <p className={`text-2xl font-bold ${c.text}`}>{value}</p>
          <p className="text-xs text-gray-500">{title}</p>
        </div>
      </div>
      {items.length > 0 && (
        <div className="space-y-1">
          {items.map((item, i) => (
            <div key={`item-${i}`} className="flex items-center justify-between text-xs">
              <span className="text-gray-600 truncate">{item.label}</span>
              <Badge className={`border-0 text-[9px] flex-shrink-0 ${c.badge}`}>{item.status}</Badge>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}